import { fetchJSON } from "lib/fetch";
import { useQuery, useMutation, queryCache, QueryConfig, MutateConfig } from "react-query";
import siteConfig from "site-config";

const TERMS = "terms";

const TERMS_LATEST = "terms-lateest";

const TERMS_SIGNED = "terms-signed";

export interface IBrandTerms {
    _id?: string;
    brand?: string;
    version?: number;
    created_on?: string;
    modified_on?: string;
    force_modified_on?: string;
    name?: string;
    description?: string;
    body?: string;
}

export type IBrandTermsUpdateRequest = IBrandTerms & { mandatory?: boolean };

interface IBrandTermsSigned {
    _id: string;
    brand: string;
    user: string;
    version_signed: number;
    created_on: string;
    modified_on: string;
    terms: string;
}

interface IBrandTermsSignedRequest {
    version: number;
    userId: string;
    terms: string;
}

interface IBrandTermsLatestSigned {
    latest: boolean;
}

export const useFetchBrandTerms = (brandId: string, mandatory: boolean, options?: QueryConfig<IBrandTerms>) => {
    return useQuery<IBrandTerms, string>(
        TERMS,
        () =>
            fetchJSON<IBrandTerms>(`${siteConfig.api}/brand/${brandId}/terms${mandatory ? "?mandatory=true" : ""}`, {
                method: "GET",
                noDomain: true,
            }),
        options,
    );
};

export const useUpdateBrandTerms = (mutationObject?: MutateConfig<IBrandTerms, Error[]>) => {
    return useMutation(updateBrandTerms, {
        ...mutationObject,
        onSuccess: (data: IBrandTerms) => {
            queryCache.invalidateQueries(TERMS);
            mutationObject?.onSuccess?.(data, {});
        },
        onError: (error, variables, snap) => {
            queryCache.invalidateQueries(TERMS);
            mutationObject?.onError?.(error, variables, snap);
        },
    });
};

export const useUpdateBrandTermsSigned = (mutationObject?: MutateConfig<IBrandTermsSigned, Error[]>) => {
    return useMutation(updateBrandTermsSigned, {
        ...mutationObject,
        onSuccess: (data: IBrandTermsSigned) => {
            queryCache.invalidateQueries(TERMS_SIGNED);
            mutationObject?.onSuccess?.(data, {});
        },
        onError: (error, variables, snap) => {
            queryCache.invalidateQueries(TERMS_SIGNED);
            mutationObject?.onError?.(error, variables, snap);
        },
    });
};

export const useFetchBrandTermsSignedLatest = (userId: string, options?: QueryConfig<IBrandTermsLatestSigned>) => {
    return useQuery<IBrandTermsLatestSigned, string>(
        TERMS_LATEST,
        () =>
            fetchJSON<IBrandTermsLatestSigned>(`${siteConfig.api}/brand-terms-latest/user/${userId}`, {
                method: "GET",
                noDomain: true,
            }),
        options,
    );
};

function updateBrandTerms(terms: IBrandTermsUpdateRequest) {
    return fetchJSON<IBrandTerms>(`${siteConfig.api}/brand/${terms.brand}/terms`, {
        method: "POST",
        noDomain: true,
        body: JSON.stringify(terms),
    });
}

function updateBrandTermsSigned(terms: IBrandTermsSignedRequest) {
    return fetchJSON<IBrandTerms>(`${siteConfig.api}/brand-terms-signed/user/${terms.userId}`, {
        method: "POST",
        noDomain: true,
        body: JSON.stringify({ version: terms.version, terms: terms.terms }),
    });
}
